<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import Code from '../components/Code.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getRecords, getResource } from '../lib/api.js';
  import { nav, go } from '../lib/nav.svelte.js';
  import { HELP } from '../lib/help.js';

  const PAGE = 30;
  let sourceId = $state(nav.params.source ?? '');
  let recordType = $state(nav.params.type ?? '');
  let period = $state(nav.params.period ?? '');
  let offset = $state(0);
  let reloadKey = $state(0);
  let selected = $state(null);

  const page = $derived.by(() => {
    reloadKey;
    return getRecords({ sourceId, recordType, period, offset, limit: PAGE });
  });

  function applyFilters() {
    offset = 0;
    go('records', { source: sourceId || null, type: recordType || null, period: period || null });
    reloadKey += 1;
  }

  async function openRecord(record) {
    selected = { ...record, ttl: 'Loading…' };
    try { selected.ttl = (await getResource(record.documentUri ?? record.uri)).ttl; }
    catch (error) { selected.ttl = `⚠ ${error.message}`; }
  }
</script>

<HelpDrawer {...HELP.records} />
<h1 class="wf2-title wf2-title-level-1">Records in Pod</h1>

{#await page}
  <div class="state"><span class="spinner"></span>Reading records…</div>
{:then data}
  <Section title="Private record inventory"><Panel>
    <div class="wf2-tablewrap"><table class="wf2-table"><thead><tr><th>Source</th><th>Record type</th><th>Container</th><th>Records</th><th>Documents</th></tr></thead><tbody>
      {#each data.counts as count}<tr><td>{count.sourceLabel}</td><td class="mono">{count.type}</td><td class="mono">{count.container}</td><td>{count.count}</td><td>{count.documentCount}</td></tr>{/each}
    </tbody></table></div>
  </Panel></Section>

  <Section title="Filter"><Panel>
    <div class="wf2-row" style="gap:12px; align-items:flex-end; flex-wrap:wrap">
      <label class="wf2-select">Source<select class="wf2-control" bind:value={sourceId}><option value="">All sources</option>{#each data.sources as source}<option value={source.id}>{source.label}</option>{/each}</select></label>
      <label class="wf2-select">Record type<select class="wf2-control" bind:value={recordType}><option value="">All types</option>{#each data.recordTypes as type}<option value={type}>{type}</option>{/each}</select></label>
      <label class="wf2-select">Period<input class="wf2-control" bind:value={period} placeholder="2026-Q1" /></label>
      <Button onclick={applyFilters}>Apply filters</Button>
    </div>
    <p class="wf2-caption">Filters and pagination apply to the complete Pod inventory.</p>
  </Panel></Section>

  {#if data.total === 0}
    <Alert title="No matching records" variant="info"><p>Run ingest or change the filters.</p></Alert>
  {:else}
    <Section title="Records"><Panel>
      <p class="wf2-text">Showing {data.offset + 1}–{Math.min(data.offset + data.rows.length, data.total)} of {data.total} private records.</p>
      <div class="wf2-tablewrap"><table class="wf2-table"><thead><tr><th>Source</th><th>Record</th><th>Type</th><th>Period</th><th>Container</th><th></th></tr></thead><tbody>
        {#each data.rows as record}<tr><td>{record.sourceLabel}</td><td class="mono">{record.id}</td><td class="mono">{record.type}</td><td>{record.period || '—'}</td><td class="mono">{record.container}</td><td><Button variant="ghost" onclick={() => openRecord(record)}>View RDF</Button></td></tr>{/each}
      </tbody></table></div>
      <div class="wf2-row" style="gap:12px"><Button disabled={offset === 0} onclick={() => { offset = Math.max(0, offset - PAGE); reloadKey += 1; }}>Previous</Button><Button disabled={offset + PAGE >= data.total} onclick={() => { offset += PAGE; reloadKey += 1; }}>Next</Button></div>
    </Panel></Section>
  {/if}
{:catch error}
  <Alert title="Could not read records" variant="danger"><p>{error.message}</p></Alert>
{/await}

{#if selected}<Section title="Record — {selected.id}"><Panel>
  <Alert title="Private {selected.sourceLabel} record" variant="info"><p>Stored with its stable RDF subject in a batch document under <span class="mono">{selected.container}</span>. Only governed aggregates can leave the Pod.</p></Alert>
  <Code lang="turtle" text={selected.ttl} />
</Panel></Section>{/if}
